import { Box, Grid, TextField, Typography, useTheme } from '@mui/material';
import { GetInspectionItem } from '../../../../../../services/apiTypes';
import CheckBox from '../../Input/CheckBox';
import React, { useEffect, useMemo, useState } from 'react';
import InspectionDetailModal, { DetailItem } from './InspectionDetailModal';
import { useDispatch, useSelector } from 'react-redux';
import {
  checkHasDetailMatrix,
  getColorByJudgement,
  isEmpty,
  isNg,
  isOk,
} from '../../../../../../_utils/inspectionUtils';
import { updateJudgement } from '../../../../../../stores/inspectionTypeData/slice';
import { useTranslation } from 'react-i18next';
import { JUDGEMENT } from '../../../../../../_constants/inspectionConst';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { TEXT_OVERFLOW_2LINES } from '../../../../../../_constants/styleConst';
import { RootState } from '../../../../../../stores/store';

type MatrixProps = {
  typeLabel: string;
  itemDataList: GetInspectionItem[];
  beforeItemDataList: GetInspectionItem[];
  isUnfilled: boolean;
  isVisualInspection: boolean;
};

/** 各項目のGridサイズ(1つ分) */
const GRID_SIZE = {
  typeLabel: 3,
  margin: 0.5, // 現象ラベルの左余白
  depth0: 2,
  detail: 3,
};

/** マトリクス形式で表示するパターン */
const Matrix: React.FC<MatrixProps> = ({
  typeLabel,
  itemDataList,
  beforeItemDataList,
  isUnfilled,
  isVisualInspection,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { categoryId, typeId } = useSelector(
    (state: RootState) => state.selectIds,
  );
  /** 現象のラベルをユニークにしたリスト */
  const [problemLabelList, setProblemLabelList] = useState<string[]>([]);
  /** 問題箇所選択ダイアログの開閉フラグ */
  const [isOpenDetailModal, setIsOpenDetailModal] = useState(false);
  /** 問題箇所選択ダイアログのタイトル */
  const [title, setTitle] = useState('');
  /** 問題箇所選択ダイアログのリスト */
  const [details, setDetails] = useState<DetailItem[]>([]);
  /**
   * 開いているダイアログのIDパス
   * path[0] : 検査部位・内容のID
   * path[1] : 現象のID
   */
  const [path, setPath] = useState<string[]>([]);

  useEffect(() => {
    const newLabelList: string[] = [];
    itemDataList.forEach(({ itemData }) =>
      itemData.forEach(({ label }) => {
        if (!newLabelList.includes(label)) {
          newLabelList.push(label);
        }
      }),
    );
    setProblemLabelList(newLabelList);
  }, [typeId]);

  useEffect(() => {
    // 縦一列全て入力した時、階層0の結果を更新する
    itemDataList.forEach((depth0) => {
      if (depth0.itemData.some(({ judgement }) => isEmpty(judgement))) {
        return;
      }
      const newJudgement = depth0.itemData.some((problem) =>
        isNg(problem.judgement),
      )
        ? JUDGEMENT.NG
        : depth0.itemData.some((problem) => isOk(problem.judgement))
          ? JUDGEMENT.OK
          : JUDGEMENT.NA;
      dispatch(
        updateJudgement({
          categoryId,
          typeId,
          path: [depth0.id],
          judgement: newJudgement,
        }),
      );
    });
  }, [itemDataList]);

  /** 問題箇所を持つ項目数*/
  const countDetailColumns = useMemo(
    () => (): number => {
      let columnsCount = 0;
      itemDataList.forEach((item) => {
        if (checkHasDetailMatrix(item.itemData)) {
          columnsCount++;
        }
      });
      return columnsCount;
    },
    [itemDataList],
  );

  const handleOpen = (item: GetInspectionItem, problem: GetInspectionItem) => {
    if (problem.itemData.length === 0) {
      return;
    }
    const newDetails: DetailItem[] = [];
    // 前回結果(階層0)
    const beforeItem = beforeItemDataList.find(
      (before0) => before0.id === item.id,
    );

    // 前回結果(階層1)
    const beforeProblem =
      beforeItem &&
      beforeItem.itemData.find((before1) => before1.id === problem.id);

    problem.itemData.forEach((detail) => {
      // 前回結果(階層2)
      const beforeDetail =
        beforeProblem &&
        beforeProblem.itemData.find((before2) => before2.id === detail.id);

      newDetails.push({
        id: detail.id,
        label: detail.label,
        isChecked: false,
        borderColor: beforeDetail && isNg(beforeDetail.judgement) ? 'red' : '',
      });
    });

    setIsOpenDetailModal(true);
    setTitle(item.label + '\t:\t' + problem.label);
    setDetails(newDetails);
    setPath([item.id, problem.id]);
  };

  const handleClose = (shouldUpdate: boolean) => {
    if (shouldUpdate) {
      const isAnyChecked = details.some((detail) => detail.isChecked);
      details.forEach((detail) => {
        // 1つもチェックが付いてない場合は空とする
        // それ以外はチェックあり→NG、チェックなし→OKとする
        const newJudgement = isAnyChecked
          ? detail.isChecked
            ? JUDGEMENT.NG
            : JUDGEMENT.OK
          : JUDGEMENT.EMPTY;
        dispatch(
          updateJudgement({
            categoryId,
            typeId,
            path: [...path, detail.id],
            judgement: newJudgement,
          }),
        );
      });
    } else {
      // キャンセルの場合は空にする
      dispatch(
        updateJudgement({
          categoryId,
          typeId,
          path: path,
          judgement: JUDGEMENT.EMPTY,
        }),
      );
      details.forEach((detail) => {
        dispatch(
          updateJudgement({
            categoryId,
            typeId,
            path: [...path, detail.id],
            judgement: JUDGEMENT.EMPTY,
          }),
        );
      });
    }
    setPath([]);
    setIsOpenDetailModal(false);
  };

  /** 問題箇所に表示するテキスト取得 */
  const getDetailText = (problem: GetInspectionItem) => {
    const labels: string[] = [];
    problem.itemData.forEach((detail) => {
      if (isNg(detail.judgement)) {
        labels.push(detail.label);
      }
    });

    return labels.join(', ');
  };

  /** 階層1のチェックボックスをOKまたはN/Aにした時 */
  const updateDetail = (
    itemId: string,
    problem: GetInspectionItem,
    judgement: number,
  ) => {
    // 問題箇所(階層2)の結果を更新
    problem.itemData.forEach((detail) => {
      dispatch(
        updateJudgement({
          categoryId,
          typeId,
          path: [itemId, problem.id, detail.id],
          judgement,
        }),
      );
    });
  };

  return (
    <Box>
      {/* ヘッダー */}
      <Grid
        container
        columns={
          // 中項目ラベルのサイズ + (階層0の数 * サイズ) + (問題箇所の数 * サイズ)
          GRID_SIZE.typeLabel +
          itemDataList.length * GRID_SIZE.depth0 +
          countDetailColumns() * GRID_SIZE.detail
        }
      >
        <Grid size={GRID_SIZE.typeLabel} mb={1} display="flex">
          {isVisualInspection && (
            <Box sx={{ display: 'inline', mx: 1.5 }}>
              <VisibilityIcon color="primary" />
            </Box>
          )}
          <Typography
            sx={[
              { fontSize: theme.typography.body1.fontSize },
              TEXT_OVERFLOW_2LINES,
            ]}
          >
            {typeLabel}
          </Typography>
        </Grid>
        {itemDataList.map((item, i) => {
          const hasDetail = checkHasDetailMatrix(item.itemData);
          return (
            <React.Fragment key={`matrix_column_${i}`}>
              <Grid
                size={GRID_SIZE.depth0}
                textAlign="center"
                alignSelf="end"
                sx={[
                  { fontSize: theme.typography.caption.fontSize },
                  TEXT_OVERFLOW_2LINES,
                ]}
              >
                {item.label}
              </Grid>
              {hasDetail && (
                <Grid
                  size={GRID_SIZE.detail}
                  textAlign="center"
                  alignSelf="end"
                >
                  <Typography
                    sx={{ fontSize: theme.typography.caption.fontSize }}
                  >
                    {t('LBL_DEFECTIVE_POSITION')}
                  </Typography>
                </Grid>
              )}
            </React.Fragment>
          );
        })}
      </Grid>

      {problemLabelList.map((problemLabel, i) => {
        const lineStyle = {
          height: '40px',
          alignItems: 'center',
          bgcolor: i % 2 === 0 ? theme.palette.background.paper : '',
        };
        return (
          <Grid
            container
            key={i}
            sx={lineStyle}
            columns={
              // 中項目ラベルのサイズ + (階層0の数 * サイズ) + (問題箇所の数 * サイズ)
              GRID_SIZE.typeLabel +
              itemDataList.length * GRID_SIZE.depth0 +
              countDetailColumns() * GRID_SIZE.detail
            }
          >
            <Grid size={GRID_SIZE.margin}></Grid>
            <Grid size={GRID_SIZE.typeLabel - GRID_SIZE.margin}>
              <Typography
                sx={[
                  { fontSize: theme.typography.caption.fontSize },
                  TEXT_OVERFLOW_2LINES,
                ]}
              >
                {problemLabel}
              </Typography>
            </Grid>
            {itemDataList.map((item, j) => {
              const hasDetail = checkHasDetailMatrix(item.itemData);
              const problem = item.itemData.find(
                (problem) => problem.label === problemLabel,
              );

              return (
                <React.Fragment key={`${item.id}_${j}`}>
                  <Grid size={GRID_SIZE.depth0} textAlign="center">
                    {problem && (
                      <CheckBox
                        itemId={`matrix_${i}-${j}`}
                        categoryId={categoryId}
                        typeId={typeId}
                        path={[item.id, problem.id]}
                        judgement={problem.judgement}
                        onOk={() =>
                          updateDetail(item.id, problem, JUDGEMENT.OK)
                        }
                        onNg={() => handleOpen(item, problem)}
                        onNa={() =>
                          updateDetail(item.id, problem, JUDGEMENT.NA)
                        }
                        borderColor={getColorByJudgement(
                          beforeItemDataList
                            .find((before) => before.id === item.id)
                            ?.itemData?.find(
                              (problem) => problem.label === problemLabel,
                            )?.judgement,
                        )}
                        isUnfilled={isUnfilled}
                      />
                    )}
                  </Grid>
                  {hasDetail && (
                    <Grid size={GRID_SIZE.detail} textAlign="center">
                      {problem && problem.itemData.length > 0 && (
                        <TextField
                          value={getDetailText(problem)}
                          sx={{
                            height: '35px',
                          }}
                          slotProps={{
                            input: {
                              readOnly: true,
                              style: {
                                height: '100%',
                                padding: '0px',
                                background:
                                  ((isNg(problem.judgement) &&
                                    getDetailText(problem) === '') ||
                                    isEmpty(problem.judgement)) &&
                                  isUnfilled
                                    ? theme.palette.warning.light
                                    : theme.palette.background.default,
                              },
                            },
                          }}
                        />
                      )}
                    </Grid>
                  )}
                </React.Fragment>
              );
            })}
          </Grid>
        );
      })}
      <InspectionDetailModal
        isOpen={isOpenDetailModal}
        title={title}
        details={details}
        setDetails={setDetails}
        handleClose={handleClose}
      />
    </Box>
  );
};

export default Matrix;
